import { useCallback, useEffect, useMemo, useState } from "react";
import { LAST_MODIFIED_KEYS } from "@/components/table/last-modified-column";

export type DropPosition = "before" | "after";

export interface ColumnOrderApi {
  /** Ordena una lista de columnas según el orden persistido. */
  orderColumns: <T extends { key: string }>(cols: T[]) => T[];
  /**
   * Mueve `fromKey` respecto a `toKey` y persiste el nuevo orden.
   * `position` indica si `fromKey` cae antes ("before", default histórico) o
   * después ("after") de `toKey`. Es opcional para no romper consumidores.
   */
  moveColumn: (fromKey: string, toKey: string, position?: DropPosition) => void;
  /** Limpia el orden personalizado (vuelve al orden por defecto de columns). */
  resetOrder: () => void;
  /** true si hay un orden personalizado guardado. */
  hasCustomOrder: boolean;
}

/**
 * Persiste el ORDEN de las columnas de una tabla en localStorage.
 * Mismo patrón que useColumnWidths ("table-column-widths-${tableId}") y
 * useColumnVisibility ("table-columns-${tableId}").
 *
 * El merge es robusto: respeta el orden guardado para las keys que aún existen,
 * agrega al final las columnas nuevas (en su orden por defecto) y descarta las
 * que ya no existen. Así, cambiar la definición de `columns` no rompe el orden
 * guardado ni pierde columnas nuevas.
 *
 * @param columns Lista COMPLETA de columnas (antes de filtrar por visibilidad),
 *   para conocer todas las keys y su orden por defecto.
 */
export function useColumnOrder(
  tableId: string | undefined,
  columns: { key: string }[],
): ColumnOrderApi {
  const storageKey = tableId ? `table-column-order-${tableId}` : null;

  const [order, setOrder] = useState<string[]>(() => {
    if (typeof window === "undefined" || !storageKey) return [];
    try {
      const raw = window.localStorage.getItem(storageKey);
      return raw ? (JSON.parse(raw) as string[]) : [];
    } catch {
      return [];
    }
  });

  // Re-leer cuando cambia tableId (cada tabla tiene su propio espacio).
  useEffect(() => {
    if (typeof window === "undefined" || !storageKey) {
      setOrder([]);
      return;
    }
    try {
      const raw = window.localStorage.getItem(storageKey);
      setOrder(raw ? (JSON.parse(raw) as string[]) : []);
    } catch {
      setOrder([]);
    }
  }, [storageKey]);

  const allKeys = useMemo(() => columns.map((c) => c.key), [columns]);

  // Orden efectivo: keys guardadas que aún existen + nuevas al final. La
  // columna de "última modificación" va siempre al final por defecto: las
  // columnas nuevas (p. ej. un campo agregado) se insertan ANTES de ella,
  // salvo que el usuario la haya movido a otra posición.
  const effectiveOrder = useMemo(() => {
    const known = new Set(allKeys);
    const fromStorage = order.filter((k) => known.has(k));
    const seen = new Set(fromStorage);
    const appended = allKeys.filter((k) => !seen.has(k));
    const isEnd = (k: string) => LAST_MODIFIED_KEYS.has(k);
    let cut = fromStorage.length;
    while (cut > 0 && isEnd(fromStorage[cut - 1])) cut--;
    return [
      ...fromStorage.slice(0, cut),
      ...appended.filter((k) => !isEnd(k)),
      ...fromStorage.slice(cut),
      ...appended.filter(isEnd),
    ];
  }, [order, allKeys]);

  const persist = useCallback(
    (next: string[]) => {
      setOrder(next);
      if (storageKey && typeof window !== "undefined") {
        try {
          window.localStorage.setItem(storageKey, JSON.stringify(next));
        } catch {
          // Sin espacio / deshabilitado: ignorar silenciosamente.
        }
      }
    },
    [storageKey],
  );

  const orderColumns = useCallback(
    <T extends { key: string }>(cols: T[]): T[] => {
      const pos = new Map(effectiveOrder.map((k, i) => [k, i]));
      // Ordenamiento estable por posición; keys desconocidas van al final.
      return [...cols].sort(
        (a, b) =>
          (pos.get(a.key) ?? Number.MAX_SAFE_INTEGER) -
          (pos.get(b.key) ?? Number.MAX_SAFE_INTEGER),
      );
    },
    [effectiveOrder],
  );

  const moveColumn = useCallback(
    (fromKey: string, toKey: string, position: DropPosition = "before") => {
      if (fromKey === toKey) return;
      const current = [...effectiveOrder];
      const fromIdx = current.indexOf(fromKey);
      const toIdx = current.indexOf(toKey);
      if (fromIdx === -1 || toIdx === -1) return;
      current.splice(fromIdx, 1);
      // Recalcular el índice tras el splice para apuntar al slot real de toKey.
      const toAfterSplice = current.indexOf(toKey);
      // "before" -> insertar en el slot actual de toKey (lo empuja a la derecha).
      // "after"  -> insertar una posición más allá (lo empuja a la izquierda).
      const insertAt = position === "after" ? toAfterSplice + 1 : toAfterSplice;
      current.splice(insertAt, 0, fromKey);
      persist(current);
    },
    [effectiveOrder, persist],
  );

  const resetOrder = useCallback(() => {
    setOrder([]);
    if (storageKey && typeof window !== "undefined") {
      try {
        window.localStorage.removeItem(storageKey);
      } catch {
        // ignore
      }
    }
  }, [storageKey]);

  return {
    orderColumns,
    moveColumn,
    resetOrder,
    hasCustomOrder: order.length > 0,
  };
}
